import React, { useContext, useState } from "react";
import { ProjectsContext } from "./ProjectsContext";
import { ChevronDown } from "lucide-react";

/* ---------- EMPTY PROJECT TEMPLATE ---------- */
const emptyProject = (id) => ({
  id,
  name: "",
  tagline: "",
  about: [""],
  location: "",
  status: "",
  category: "residential",
  mapEmbedUrl: "",
  images: [],
  highlights: [""],
  amenities: [""],
  locationAdvantages: [""],
  whyChoose: [""],
  approvals: [""],
  projectInfo: [{ key: "", value: "" }],
});

/* ---------- COMPONENT ---------- */
export default function ProjectsAdmin() {
  const { projects, addProject, updateProject, deleteProject, loading } =
    useContext(ProjectsContext);

  const [formData, setFormData] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [filterLabel, setFilterLabel] = useState("All Projects");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [errors, setErrors] = useState({});

  /* ---------- ACTIONS ---------- */
  const startAdd = () => {
    setFormData(emptyProject(Date.now()));
    setIsAdding(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEdit = (project) => {
    const p = JSON.parse(JSON.stringify(project));

    if (!Array.isArray(p.projectInfo)) p.projectInfo = [];
    if (!Array.isArray(p.about)) p.about = [""];
    if (!Array.isArray(p.highlights)) p.highlights = [""];
    if (!Array.isArray(p.amenities)) p.amenities = [""];
    if (!Array.isArray(p.locationAdvantages)) p.locationAdvantages = [""];
    if (!Array.isArray(p.whyChoose)) p.whyChoose = [""];
    if (!Array.isArray(p.approvals)) p.approvals = [""];

    setFormData(p);
    setIsAdding(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelForm = () => {
    setFormData(null);
    setIsAdding(false);
  };

  const saveProject = async () => {
    const newErrors = {};

    if (!formData.name?.trim()) newErrors.name = "Project name is required";
    if (!formData.location?.trim()) newErrors.location = "Location is required";
    if (!formData.tagline?.trim()) newErrors.tagline = "Tagline is required";
    if (!formData.status?.trim()) newErrors.status = "Status is required";
    if (!formData.category?.trim()) newErrors.category = "Category is required";
    if (!formData.mapEmbedUrl?.trim())
      newErrors.mapEmbedUrl = "Map URL is required";

    if (!formData.about || formData.about.filter((a) => a.trim()).length === 0)
      newErrors.about = "At least one About line is required";

    if (!formData.images || formData.images.length === 0)
      newErrors.images = "At least one image is required";

    [
      "highlights",
      "amenities",
      "whyChoose",
      "locationAdvantages",
      "approvals",
    ].forEach((key) => {
      if (
        !formData[key] ||
        formData[key].filter((v) => v.trim()).length === 0
      ) {
        newErrors[key] = `At least one ${key} item is required`;
      }
    });

    if (
      !formData.projectInfo ||
      formData.projectInfo.filter((i) => i.key && i.value).length === 0
    ) {
      newErrors.projectInfo =
        "At least one Project Information entry is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const cleanedData = {
      ...formData,
      projectInfo: formData.projectInfo.filter(
        (item) => item.key && item.value
      ),
    };

    const exists = projects.some((p) => p.id === cleanedData.id);
    exists ? await updateProject(cleanedData) : await addProject(cleanedData);

    cancelForm();
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("imageIndex", index);
  };

  const handleDrop = (e, index) => {
    const fromIndex = e.dataTransfer.getData("imageIndex");
    if (fromIndex === null) return;

    const updated = [...formData.images];
    const movedItem = updated[fromIndex];
    updated.splice(fromIndex, 1);
    updated.splice(index, 0, movedItem);

    setFormData({ ...formData, images: updated });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  /* ---------- HANDLERS ---------- */
  const handleChange = (k, v) => setFormData({ ...formData, [k]: v });

  const handleArrayAdd = (key) =>
    setFormData({ ...formData, [key]: [...formData[key], ""] });

  const handleArrayChange = (key, i, v) => {
    const copy = [...formData[key]];
    copy[i] = v;
    setFormData({ ...formData, [key]: copy });
  };

  const handleArrayRemove = (key, i) => {
    const copy = [...formData[key]];
    copy.splice(i, 1);
    setFormData({ ...formData, [key]: copy });
  };

  const handleImageUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () =>
      setFormData({
        ...formData,
        images: [...formData.images, reader.result],
      });
    reader.readAsDataURL(file);
  };

  const addProjectInfo = () => {
    setFormData({
      ...formData,
      projectInfo: [...formData.projectInfo, { key: "", value: "" }],
    });
  };

  const updateProjectInfo = (index, field, value) => {
    const copy = [...formData.projectInfo];
    copy[index][field] = value;
    setFormData({ ...formData, projectInfo: copy });
  };

  const removeProjectInfo = (index) => {
    const copy = [...formData.projectInfo];
    copy.splice(index, 1);
    setFormData({ ...formData, projectInfo: copy });
  };

  /* ---------- FILTER ---------- */
  const filteredProjects = projects.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.location.toLowerCase().includes(search.toLowerCase());

    const matchesCategory =
      categoryFilter === "all" || p.category === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="p-10 text-center text-[var(--brand-dark)]">
        Loading projects...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-1)] p-6 md:p-10">
      {/* ================= HEADER ================= */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-10">
        {/* LEFT: Title */}
        <div className="flex flex-col">
          <h1 className="text-3xl md:text-4xl font-serif text-[var(--brand-dark)] leading-tight">
            Projects Management
          </h1>
          <p className="text-sm text-gray-500">
            Manage all residential & farmland projects
          </p>
        </div>

        {/* RIGHT: Search + Filter + Button */}
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          {/* Search */}
          <input
            placeholder="Search by name or location"
            className="border rounded-md px-4 py-2 w-full sm:w-64 focus:outline-none focus:border-[var(--brand-gold)]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* FILTER DROPDOWN */}
          <div className="relative w-full sm:w-44">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="w-full border px-4 py-2 rounded-md bg-white flex items-center justify-between"
            >
              {filterLabel}
              <ChevronDown size={16} />
            </button>

            {showFilterDropdown && (
              <div className="absolute right-0 mt-2 w-full bg-white border rounded shadow z-10">
                {[
                  { label: "All Projects", value: "all" },
                  { label: "Residential", value: "residential" },
                  { label: "Farmland", value: "farmland" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setCategoryFilter(opt.value);
                      setFilterLabel(opt.label);
                      setShowFilterDropdown(false);
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ADD BUTTON */}
          <button
            onClick={startAdd}
            className="bg-[var(--brand-gold)] text-white px-6 py-2 rounded-md shadow hover:shadow-lg transition whitespace-nowrap"
          >
            + Add Project
          </button>
        </div>
      </div>

      {/* ================= FORM CARD ================= */}
      {formData && (
        <div className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-10 border border-[var(--bg-3)]">
          <h2 className="text-xl font-semibold text-[var(--brand-dark)] mb-6">
            {isAdding ? "Add New Project" : "Edit Project"}
          </h2>

          {/* BASIC INFO */}
          <div className="grid md:grid-cols-2 gap-5 mb-6">
            {[
              ["Project Name", "name"],
              ["Location", "location"],
              ["Tagline", "tagline"],
              ["Status", "status"],
            ].map(([label, key]) => (
              <div key={key}>
                <label className="text-sm font-medium text-[var(--brand-dark)]">
                  {label} <span className="text-red-500">*</span>
                </label>
                <input
                  className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                  value={formData[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                />
                {errors[key] && (
                  <p className="text-red-600 text-sm mt-1">{errors[key]}</p>
                )}
              </div>
            ))}

            {/* Category */}
            <div>
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                value={formData.category}
                onChange={(e) => handleChange("category", e.target.value)}
              >
                <option value="">Select Category</option>
                <option value="residential">Residential</option>
                <option value="farmland">Farmland</option>
              </select>
              {errors.category && (
                <p className="text-red-600 text-sm mt-1">{errors.category}</p>
              )}
            </div>

            {/* Map */}
            <div>
              <label className="text-sm font-medium text-[var(--brand-dark)]">
                Google Map Embed URL <span className="text-red-500">*</span>
              </label>
              <input
                className="mt-1 w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
                value={formData.mapEmbedUrl}
                onChange={(e) => handleChange("mapEmbedUrl", e.target.value)}
              />
              {errors.mapEmbedUrl && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.mapEmbedUrl}
                </p>
              )}
            </div>
          </div>

          {/* ABOUT */}
          {renderArraySection(
            "About Project",
            "about",
            formData,
            handleArrayChange,
            handleArrayAdd,
            handleArrayRemove,
            errors
          )}
          {/* IMAGES */}
          <div className="mb-8">
            <label className="text-sm font-medium text-[var(--brand-dark)] mb-2 block">
              Images <span className="text-red-500">*</span>
            </label>

            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) =>
                Array.from(e.target.files).forEach(handleImageUpload)
              }
            />
            {errors.images && (
              <p className="text-red-600 text-sm mt-1">{errors.images}</p>
            )}

            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-3">
              {formData.images.map((img, i) => (
                <div
                  key={i}
                  draggable
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, i)}
                  className="relative cursor-move border-2 border-dashed border-transparent hover:border-[var(--brand-gold)] rounded"
                >
                  <img
                    src={img}
                    alt=""
                    className="h-24 w-full object-cover rounded"
                  />

                  <button
                    onClick={() => handleArrayRemove("images", i)}
                    className="absolute top-1 right-1 bg-red-600 text-white px-2 rounded"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* ARRAYS */}
          {[
            ["Highlights", "highlights"],
            ["Amenities", "amenities"],
            ["Why Choose", "whyChoose"],
            ["Location Advantages", "locationAdvantages"],
            ["Approvals", "approvals"],
          ].map(([label, key]) =>
            renderArraySection(
              label,
              key,
              formData,
              handleArrayChange,
              handleArrayAdd,
              handleArrayRemove,
              errors
            )
          )}

          {/* PROJECT INFO */}
          <div className="mb-8">
            <label className="text-sm font-medium text-[var(--brand-dark)] mb-2 block">
              Project Information <span className="text-red-500">*</span>
            </label>
            {errors.projectInfo && (
              <p className="text-red-600 text-sm mb-2">{errors.projectInfo}</p>
            )}

            {formData.projectInfo.map((item, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  placeholder="Label"
                  className="w-1/3 border rounded-md px-3 py-2"
                  value={item.key}
                  onChange={(e) => updateProjectInfo(i, "key", e.target.value)}
                />
                <input
                  placeholder="Value"
                  className="flex-1 border rounded-md px-3 py-2"
                  value={item.value}
                  onChange={(e) =>
                    updateProjectInfo(i, "value", e.target.value)
                  }
                />
                <button
                  onClick={() => removeProjectInfo(i)}
                  className="px-3 rounded bg-red-500 text-white"
                >
                  ×
                </button>
              </div>
            ))}

            <button
              onClick={addProjectInfo}
              className="text-sm text-[var(--brand-gold)] mt-1"
            >
              + Add Info
            </button>
          </div>

          {/* ACTIONS */}
          <div className="flex gap-4">
            <button
              onClick={saveProject}
              className="bg-[var(--brand-dark)] text-white px-6 py-2 rounded-md hover:opacity-90 transition"
            >
              Save Project
            </button>
            <button
              onClick={cancelForm}
              className="bg-gray-300 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-400 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ================= TABLE CARD ================= */}
      <div className="bg-white rounded-xl shadow-md p-6 md:p-8 border border-[var(--bg-3)]">
        <h2 className="text-xl font-semibold text-[var(--brand-dark)] mb-6">
          All Projects
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-3)] text-[var(--brand-dark)]">
                <th className="text-left p-3 font-semibold">Name</th>
                <th className="text-left p-3 font-semibold">Location</th>
                <th className="text-left p-3 font-semibold">Category</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((p) => (
                <tr
                  key={p.id}
                  className="border-b last:border-none hover:bg-[var(--bg-2)] transition"
                >
                  <td className="p-3">{p.name}</td>
                  <td className="p-3">{p.location}</td>
                  <td className="p-3">{p.category}</td>
                  <td className="p-3">{p.status}</td>
                  <td className="p-3 text-right space-x-2">
                    <button
                      onClick={() => startEdit(p)}
                      className="text-[var(--brand-gold)] font-medium hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setProjectToDelete(p)}
                      className="text-red-600 font-medium hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {filteredProjects.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-6 text-center text-gray-500">
                    No projects found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= DELETE MODAL ================= */}
      {projectToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-lg">
            <h3 className="text-lg font-semibold text-[var(--brand-dark)] mb-3">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <b>{projectToDelete.name}</b>?
              This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setProjectToDelete(null)}
                className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await deleteProject(projectToDelete.id);
                  setProjectToDelete(null);
                }}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- REUSABLE ARRAY SECTION ---------- */
function renderArraySection(
  title,
  keyName,
  formData,
  handleArrayChange,
  handleArrayAdd,
  handleArrayRemove,
  errors
) {
  return (
    <div className="mb-8">
      <label className="text-sm font-medium text-[var(--brand-dark)] mb-2 block">
        {title} <span className="text-red-500">*</span>
      </label>

      {formData[keyName].map((item, index) => (
        <div key={index} className="flex gap-2 mb-2">
          <input
            className="w-full border rounded-md px-4 py-2 focus:outline-none focus:border-[var(--brand-gold)]"
            value={item}
            onChange={(e) => handleArrayChange(keyName, index, e.target.value)}
          />
          <button
            onClick={() => handleArrayRemove(keyName, index)}
            className="px-3 rounded bg-red-500 text-white"
          >
            ×
          </button>
        </div>
      ))}

      <button
        onClick={() => handleArrayAdd(keyName)}
        className="text-sm text-[var(--brand-gold)] mt-1"
      >
        + Add {title}
      </button>
      {errors?.[keyName] && (
        <p className="text-red-600 text-sm mt-1">{errors[keyName]}</p>
      )}
    </div>
  );
}
